import { useMutation } from "@tanstack/react-query";
import {
  Form,
  minLength,
  required,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import type { CrmDataProvider } from "../providers/types";

/**
 * «Всё видно»: the password is changed in place (current + new) instead of
 * Supabase's "reset by email" flow.
 */
export const ChangePasswordDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider<CrmDataProvider>();

  const { mutate, isPending } = useMutation({
    mutationKey: ["updatePassword"],
    mutationFn: async (values: { current: string; next: string }) => {
      if (!identity) {
        throw new Error(
          translate("crm.profile.record_not_found", { _: "Record not found" }),
        );
      }
      return dataProvider.updatePassword(identity.id, values);
    },
    onSuccess: () => {
      notify("crm.profile.password.changed", {
        messageArgs: { _: "Your password has been changed" },
      });
      onClose();
    },
    onError: (e: Error) => {
      notify(e.message || `${e}`, { type: "error" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogTitle>{translate("crm.profile.password.change")}</DialogTitle>
        {/* the dialog is portaled, but React still bubbles submit events to
            the page's own form (the profile form): stop them here */}
        <div onSubmit={(e) => e.stopPropagation()}>
          <Form
            className="space-y-4"
            onSubmit={(values: Record<string, string>) =>
              mutate({ current: values.current, next: values.next })
            }
          >
            <TextInput
              source="current"
              label="crm.profile.password.current"
              type="password"
              autoComplete="current-password"
              validate={required()}
              helperText={false}
            />
            <TextInput
              source="next"
              label="crm.profile.password.new"
              type="password"
              autoComplete="new-password"
              validate={[required(), minLength(8)]}
              helperText={false}
            />
            <div className="flex justify-end">
              <Button type="submit" disabled={isPending}>
                {translate("ra.action.save")}
              </Button>
            </div>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
